import { put, call, takeLatest, select } from 'redux-saga/effects';
import moment from 'moment';
import { AxiosError } from 'axios';
import { API } from '../api/base';
import { types, CityPassTicket, ValidateTicketRequest } from './types';
import { getErrorMessageCode, getErrorMessage } from 'services/api/normalizer';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { selectors } from 'services/cityPass';
import get from 'lodash.get';
import i18n from 'services/translation/i18n';

const cpStatusCodes = [
  'Success',
  'NoMatchFound',
  'BarcodePreviouslyConsumed',
  'Disabled',
  'InvalidatedAtSplitAttraction',
  'InvalidUsageDate',
  'SystemFailure',
  'Unexpected',
  'NotValidated',
  'OldBarcode',
  'WrongCity',
  'BadFormat',
  'NoRemainingUsages',
  'ExpiredDaysSinceFirstUsage',
  'ExpiredDaysSinceSale',
  'ExpiredProductExpirationDate',
  'UsageOccuredInBufferWindow',
  'InvalidAttractionProductCode',
  'InvalidSecurityCode',
  'InvalidAnchorAttractionUsage',
  'GuestCenterReservationFailure',
  'InvalidInput'
];

export function* validateTicketAsync({ payload }: ValidateTicketRequest) {
  yield put({ type: types.VALIDATE_TICKET.PENDING, payload: true });

  try {
    const cityPassTickets: CityPassTicket[] = yield select(selectors.selectTickets);

    const validateAPI = new API('cityPass/validate');
    const response: CityPassTicket = yield call(validateAPI.create, {
      barcode: payload,
      usageDate: moment().format()
    });

    if (cityPassTickets.length) {
      if (response.productFamilyName !== cityPassTickets[0].productFamilyName) {
        throw new Error('cpTypeMismatch');
      }
    }

    yield put({ type: types.VALIDATE_TICKET.SUCCESS, payload: response });
  } catch (err) {
    const code = getErrorMessageCode(err as AxiosError);
    let message = getErrorMessage(err as AxiosError);
    const cpStatusCode = cpStatusCodes[code];
    if (
      [
        'BarcodePreviouslyConsumed',
        'Disabled',
        'InvalidatedAtSplitAttraction',
        'InvalidUsageDate',
        'NotValidated',
        'OldBarcode',
        'WrongCity',
        'ExpiredDaysSinceFirstUsage',
        'ExpiredDaysSinceSale',
        'ExpiredProductExpirationDate',
        'UsageOccuredInBufferWindow',
        'InvalidAttractionProductCode',
        'InvalidSecurityCode',
        'InvalidAnchorAttractionUsage',
        'GuestCenterReservationFailure',
        'NoRemainingUsages'
      ].includes(cpStatusCode)
    ) {
      message = i18n.t('sagaError.cpInvalid');
    } else if (message === 'cpTypeMismatch') {
      message = i18n.t('sagaError.cpTypeMismatch');
    } else {
      const config: Configuration = yield select(configSelectors.selectConfiguration);
      const support = get(config, `ticketing.supportName.${i18n.language}`) || i18n.t('commonText.support');
      message = i18n.t('sagaError.cpRetryCode', { support });
    }
    yield put({ type: types.VALIDATE_TICKET.ERROR, payload: message });
  } finally {
    yield put({ type: types.VALIDATE_TICKET.PENDING, payload: false });
  }
}

export const sagas = [takeLatest(types.VALIDATE_TICKET.REQUEST, validateTicketAsync)];
